import * as T from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { CHAMBER, capsuleLayout } from './machine-capsules.js';

const plastics=new Map(),capsuleGeometry=new Map();
const plastic=(color,roughness=.27)=>{
  const key=`${color}:${roughness}`;
  if(!plastics.has(key))plastics.set(key,new T.MeshPhysicalMaterial({color,roughness,clearcoat:.65,clearcoatRoughness:.18}));
  return plastics.get(key);
};
function mesh(g,m,x=0,y=0,z=0){
  const o=new T.Mesh(g,m);o.position.set(x,y,z);o.castShadow=true;o.receiveShadow=true;return o;
}
function roundedOutline(width,height,radius){
  const x=-width/2,y=-height/2,r=Math.min(radius,width/2,height/2),s=new T.Shape();
  s.moveTo(x+r,y);s.lineTo(x+width-r,y);s.quadraticCurveTo(x+width,y,x+width,y+r);
  s.lineTo(x+width,y+height-r);s.quadraticCurveTo(x+width,y+height,x+width-r,y+height);
  s.lineTo(x+r,y+height);s.quadraticCurveTo(x,y+height,x,y+height-r);
  s.lineTo(x,y+r);s.quadraticCurveTo(x,y,x+r,y);return s;
}
function plaque(width,height,depth,radius,hole){
  const outline=roundedOutline(width,height,radius);
  if(hole)outline.holes.push(roundedOutline(...hole));
  const geometry=new T.ExtrudeGeometry(outline,{depth,steps:1,curveSegments:10,bevelEnabled:true,bevelThickness:.012,bevelSize:.012,bevelSegments:3});
  geometry.translate(0,0,-depth/2);return geometry;
}
function lathe(profile,segments=80){
  return new T.LatheGeometry(profile.map(([r,y])=>new T.Vector2(r,y)),segments);
}
function horizontalRing(radius,tube,y,material){
  const ring=mesh(new T.TorusGeometry(radius,tube,8,80),material,0,y,0);ring.rotation.x=Math.PI/2;return ring;
}
function batchStatic(parts,parent){
  // Details remain separate while authored, then share one draw per finish.
  const batches=new Map();
  for(const part of parts){
    part.updateMatrix();
    const geometry=part.geometry.index?part.geometry.toNonIndexed():part.geometry.clone();
    geometry.applyMatrix4(part.matrix);
    const key=`${part.material.uuid}:${part.castShadow}`;
    if(!batches.has(key))batches.set(key,{material:part.material,castShadow:part.castShadow,geometries:[]});
    batches.get(key).geometries.push(geometry);
  }
  for(const {material,castShadow,geometries} of batches.values()){
    const combined=mesh(mergeGeometries(geometries),material);combined.name=`Machine ${material.name||'trim'}`;
    combined.castShadow=castShadow;parent.add(combined);
    for(const geometry of geometries)geometry.dispose();
  }
}

export function capsule(radius=.34,color='#bda0eb') {
  if(!capsuleGeometry.has(radius))capsuleGeometry.set(radius,[
    new T.SphereGeometry(radius,32,16,0,Math.PI*2,0,Math.PI/2),
    new T.SphereGeometry(radius,32,16,0,Math.PI*2,Math.PI/2,Math.PI/2),
    new T.TorusGeometry(radius,.012,6,48),
  ]);
  const [upper,lower,seam]=capsuleGeometry.get(radius);
  const group=new T.Group(),top=mesh(upper,plastic(color,.24)),bottom=mesh(lower,plastic('#fff7ee',.28));
  const ring=mesh(seam,plastic('#fffdf8',.31));
  ring.rotation.x=Math.PI/2;group.add(top,bottom,ring);group.userData={top,bottom,ring};return group;
}

export function makeMachine(){
  const group=new T.Group(),cream=plastic('#fff5e7',.29),lilac=plastic('#b5a1d1',.26),rose=plastic('#e2b8b3',.24);
  cream.name='warm enamel';lilac.name='lilac enamel';rose.name='rose enamel';
  const gasket=new T.MeshStandardMaterial({name:'soft gasket',color:'#9b8aaa',roughness:.72});
  const metal=new T.MeshStandardMaterial({name:'satin hardware',color:'#d8d0c5',metalness:.78,roughness:.32});
  const recess=new T.MeshStandardMaterial({name:'chute interior',color:'#4b3e53',roughness:.86});
  const parts=[];
  parts.push(mesh(lathe([[0,.02],[.85,.02],[.96,.035],[1.005,.075],[1.012,.13],[.99,.185],[.94,.215],[0,.215]]),lilac));
  parts.push(horizontalRing(.94,.016,.036,gasket));

  // A continuous filleted cabinet, with an actual opening behind the flap.
  const wall=new T.CatmullRomCurve3([[.86,.21],[.92,.26],[.95,.40],[.94,.67],[.91,.95],[.86,1.28],[.80,1.51]].map(([x,y])=>new T.Vector3(x,y,0)));
  const profile=[[0,.21],...wall.getPoints(52).map(p=>[p.x,p.y]),[0,1.51]];
  const cabinet=lathe(profile,80),position=cabinet.attributes.position,indices=cabinet.index.array,kept=[];
  for(let i=0;i<indices.length;i+=3){
    const a=indices[i],b=indices[i+1],c=indices[i+2];
    const x=(position.getX(a)+position.getX(b)+position.getX(c))/3;
    const y=(position.getY(a)+position.getY(b)+position.getY(c))/3;
    const z=(position.getZ(a)+position.getZ(b)+position.getZ(c))/3;
    if(!(Math.abs(x)<.36&&y>.245&&y<.79&&z>.6))kept.push(a,b,c);
  }
  cabinet.setIndex(kept);parts.push(mesh(cabinet,cream));
  parts.push(mesh(lathe([[0,1.50],[.80,1.50],[.89,1.52],[.93,1.565],[.93,1.625],[.88,1.68],[.79,1.735],[.78,1.765],[0,1.765]]),lilac));
  parts.push(horizontalRing(.778,.018,1.765,cream));
  parts.push(horizontalRing(.916,.012,1.625,cream));

  const glass=new T.MeshPhysicalMaterial({name:'machine_glass',color:'#ffffff',roughness:.025,transmission:1,thickness:.025,ior:1.10,clearcoat:.7,clearcoatRoughness:.035,envMapIntensity:.85});
  const domeRadius=CHAMBER.outerRadius,topAngle=Math.acos((3.28-CHAMBER.centerY)/domeRadius),bottomAngle=Math.acos((1.765-CHAMBER.centerY)/domeRadius);
  const dome=mesh(new T.SphereGeometry(domeRadius,96,48,0,Math.PI*2,topAngle,bottomAngle-topAngle),glass,0,CHAMBER.centerY,0);
  dome.name='Clear acrylic globe';dome.castShadow=false;dome.receiveShadow=false;group.add(dome);
  parts.push(mesh(lathe([[0,3.245],[.59,3.245],[.635,3.27],[.652,3.30],[.63,3.335],[.53,3.35],[0,3.35]]),cream));
  parts.push(horizontalRing(.629,.014,3.277,lilac));
  parts.push(mesh(new T.SphereGeometry(.135,32,20),rose,0,3.415,0));

  parts.push(mesh(plaque(.94,.66,.18,.16),lilac,0,1.145,.775));
  for(const x of [-.34,.34]){
    const screw=mesh(new T.CylinderGeometry(.023,.023,.012,16),metal,x,1.365,.886);screw.rotation.x=Math.PI/2;parts.push(screw);
    parts.push(mesh(new RoundedBoxGeometry(.022,.005,.005,1,.002),gasket,x,1.365,.895));
  }
  const knob=new T.Group();knob.position.set(0,1.1,.91);
  const bezel=mesh(new T.TorusGeometry(.238,.014,8,64),metal,0,0,.003);
  const rim=mesh(new T.CylinderGeometry(.222,.235,.082,64),cream);rim.rotation.x=Math.PI/2;
  const dial=mesh(new T.CylinderGeometry(.180,.180,.010,64),lilac,0,0,.047);dial.rotation.x=Math.PI/2;
  const handle=mesh(new RoundedBoxGeometry(.405,.108,.145,5,.049),rose,0,0,.115);
  const hub=mesh(new T.SphereGeometry(.081,24,16),rose,0,0,.102);hub.scale.z=.72;
  knob.add(bezel,rim,dial,handle,hub);group.add(knob);

  const cavity=mesh(plaque(.73,.555,.04,.075),recess,0,.516,.85);cavity.castShadow=false;parts.push(cavity);
  parts.push(mesh(plaque(.84,.65,.13,.14,[.675,.535,.083]),lilac,0,.52,.914));
  const hinge=mesh(new T.CylinderGeometry(.024,.024,.70,20),metal,0,.792,.938);hinge.rotation.z=Math.PI/2;parts.push(hinge);
  const flap=new T.Group();flap.position.set(0,.79,.935);
  const door=mesh(plaque(.645,.50,.025,.07),plastic('#c9b8dc',.23),0,-.25,0);
  const grip=mesh(new RoundedBoxGeometry(.23,.024,.014,3,.007),cream,0,-.425,.026);
  flap.add(door,grip);group.add(flap);
  parts.push(mesh(new RoundedBoxGeometry(.80,.075,.54,4,.035),lilac,0,.23,1.13));
  parts.push(mesh(new RoundedBoxGeometry(.69,.018,.45,3,.009),cream,0,.274,1.125));
  for(const x of [-.4,.4])parts.push(mesh(new RoundedBoxGeometry(.055,.19,.54,4,.025),cream,x,.31,1.13));
  batchStatic(parts,group);

  const colors=['#d1bbea','#f0bbb7','#bad6c8','#f0d19d','#c0ccea'],balls=[];
  for(const [i,position] of capsuleLayout().entries()){
    const ball=capsule(CHAMBER.capsuleRadius,colors[i%colors.length]);ball.position.copy(position);ball.rotation.set(i*.8,0,i*.54);
    group.add(ball);balls.push({ball,rest:ball.position.clone()});
  }
  group.rotation.y=0;
  return {group,knob,balls,flap};
}
